import type { SupplierIntegrationEntry } from '@choco-admin/types';

/**
 * The email addresses to show for a distributor in the list.
 *
 * A distributor carries emails in two unrelated places, and the two admin apps
 * were reading different ones. Legacy shows the addresses configured on the
 * email integration, which are the ones orders and messages are actually sent
 * to. The portal showed `contactEmails`, a separate enquiry address. They are
 * populated independently, so each app displayed an address where the other
 * showed "No contact email" (GitHub #95).
 *
 * Integration emails win, matching legacy. `contactEmails` is kept as a
 * fallback rather than dropped, so no distributor that shows an address today
 * stops showing one.
 */

const isEmailOption = (
  entry: SupplierIntegrationEntry
): entry is Extract<
  SupplierIntegrationEntry,
  { __typename: 'IntegrationEmailOption' }
> => entry.__typename === 'IntegrationEmailOption';

interface SupplierEmailSources {
  integration?: readonly SupplierIntegrationEntry[] | null;
  contactEmails?: readonly string[] | null;
}

/**
 * Every address on the distributor's email integration, in the order legacy
 * lists them: order, then message, then complaint, deduplicated.
 *
 * Only the first email option is read, mirroring legacy's `find`. The backend
 * models `integration` as a list, but a distributor has at most one email
 * option in practice.
 */
export const getIntegrationEmails = (
  integration?: readonly SupplierIntegrationEntry[] | null
): string[] => {
  const emailOption = integration?.find(isEmailOption);
  if (!emailOption) return [];

  return Array.from(
    new Set([
      ...(emailOption.orderEmails ?? []),
      ...(emailOption.messageEmails ?? []),
      ...(emailOption.complaintEmails ?? []),
    ])
  ).filter((email) => email.trim().length > 0);
};

/**
 * The single line shown under a distributor's name in the list, or undefined
 * when the distributor has no address anywhere.
 */
export const getSupplierListEmails = ({
  integration,
  contactEmails,
}: SupplierEmailSources): string | undefined => {
  const integrationEmails = getIntegrationEmails(integration);
  if (integrationEmails.length > 0) return integrationEmails.join(', ');

  const contactEmail = contactEmails?.find((email) => email.trim().length > 0);
  return contactEmail;
};
